/**
 * Central coordinator that manages all Algolia tracking operations through specialized components
 * Provides unified API while delegating specific concerns to focused classes
 */

import { AlgoliaUserTokenConfig, ProductSpecData } from './interfaces';
import { AlgoliaConfigResolver } from './AlgoliaConfigResolver';
import { AuthenticationDetector } from './AuthenticationDetector';
import { AlgoliaTokenManager } from './AlgoliaTokenManager';
import { AlgoliaClientManager } from './AlgoliaClientManager';
import { AlgoliaEventTracker } from './AlgoliaEventTracker';

/**
 * Singleton orchestrator coordinating specialized tracking components
 * Maintains backward compatibility while using modern modular architecture
 */
export class ApoAlgoliaTracking {
  private static instance: ApoAlgoliaTracking;
  private isInitialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private purchaseTrackingCompleted: boolean = false;
  
  // Event deduplication to handle React StrictMode double-invocation
  private recentEvents: Map<string, number> = new Map();
  private readonly DEDUP_WINDOW_MS = 100; // 100ms deduplication window

  // Component dependencies
  private readonly configResolver: AlgoliaConfigResolver;
  private readonly authDetector: AuthenticationDetector;
  private readonly tokenManager: AlgoliaTokenManager;
  private readonly clientManager: AlgoliaClientManager;
  private readonly eventTracker: AlgoliaEventTracker;

  /**
   * Creates and wires together all tracking components with dependency injection
   */
  private constructor() {
    // Initialize components with proper dependency injection
    this.configResolver = new AlgoliaConfigResolver();
    this.authDetector = new AuthenticationDetector();
    this.tokenManager = new AlgoliaTokenManager(this.authDetector);
    this.clientManager = new AlgoliaClientManager(this.configResolver);
    this.eventTracker = new AlgoliaEventTracker(this.configResolver, this.tokenManager);
  }

  /**
   * Returns the singleton instance of ApoAlgoliaTracking
   * @returns The singleton instance
   */
  public static getInstance(): ApoAlgoliaTracking {
    if (!ApoAlgoliaTracking.instance) {
      ApoAlgoliaTracking.instance = new ApoAlgoliaTracking();
    }
    return ApoAlgoliaTracking.instance;
  }

  /**
   * Bootstraps all components and establishes user token configuration
   * @returns Promise that resolves when initialization is complete
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  /**
   * Executes client setup and user token configuration in sequence
   * @returns Promise that resolves when initialization is complete
   */
  private async performInitialization(): Promise<void> {
    // Initialize Algolia Insights client
    this.clientManager.initializeInsightsClient();

    // Setup user token configuration
    const insightsClient = this.clientManager.getInsightsClient();
    await this.tokenManager.setupUserToken(insightsClient);

    // Mark as initialized
    this.isInitialized = true;
  }

  /**
   * Gets the current user token configuration
   * @returns The user token configuration or null if not initialized
   */
  public getUserTokenConfig(): AlgoliaUserTokenConfig | null {
    return this.tokenManager.getUserTokenConfig();
  }

  /**
   * Checks if the tracking system is initialized and ready
   * @returns True if initialized, false otherwise
   */
  public isReady(): boolean {
    return this.isInitialized &&
           this.clientManager.isReady() &&
           this.tokenManager.getUserTokenConfig() !== null;
  }

  /**
   * Checks if an event is a duplicate within the deduplication window
   * Helps prevent React StrictMode double-invocation issues
   * @param eventKey - Unique key for the event
   * @param customWindowMs - Optional custom deduplication window (default: 100ms)
   * @returns True if event should be deduplicated (skipped)
   */
  private isDuplicateEvent(eventKey: string, customWindowMs?: number): boolean {
    const now = Date.now();
    const lastEventTime = this.recentEvents.get(eventKey);
    const windowMs = customWindowMs || this.DEDUP_WINDOW_MS;
    
    if (lastEventTime && (now - lastEventTime) < windowMs) {
      return true;
    }
    
    // Record this event and clean up old entries
    this.recentEvents.set(eventKey, now);
    
    // Clean up entries older than dedup window to prevent memory leaks
    for (const [key, timestamp] of this.recentEvents.entries()) {
      if ((now - timestamp) > windowMs * 2) {
        this.recentEvents.delete(key);
      }
    }
    
    return false;
  }


  // =============================================================================
  // Event Tracking Methods - All delegate to AlgoliaEventTracker
  // =============================================================================

  /**
   * Tracks a cart button click event for Algolia insights
   * @param algoliaQueryId - The Algolia query ID associated with the product
   * @param pzn - The product PZN (Pharmazentralnummer) identifier
   * @param source - The source of the cart action (e.g., 'userjourney', 'recommendation')
   */
  public trackCartButtonClick(algoliaQueryId: string, pzn: string, source: string = 'conversion'): void {
    if (!this.isReady()) {
      console.warn('ApoAlgoliaTracking not ready for cart button click tracking', {
        isInitialized: this.isInitialized,
        clientReady: this.clientManager.isReady(),
        hasUserToken: this.tokenManager.getUserTokenConfig() !== null
      });
      return;
    }
    
    console.log('ApoAlgoliaTracking is ready - tracking cart button click');

    // Create unique event key for deduplication
    const eventKey = `cart_click:${pzn}:${algoliaQueryId}:${source}`;
    if (this.isDuplicateEvent(eventKey)) {
      return;
    }

    const insightsClient = this.clientManager.getInsightsClient();
    this.eventTracker.trackCartButtonClick(algoliaQueryId, pzn, source, insightsClient);
  }

  /**
   * Tracks a purchase event for Algolia insights
   * @param products - Products array as JSON encoded object string
   * @param orderTotal - Order total value as string
   * @param source - The source of the purchase (e.g., 'userjourney', 'recommendation')
   */
  public trackPurchaseEvent(products: string, orderTotal: string, source: string = 'conversion'): void {
    if (!this.isReady()) {
      console.warn('ApoAlgoliaTracking not ready for purchase tracking', {
        isInitialized: this.isInitialized,
        clientReady: this.clientManager.isReady(),
        hasUserToken: this.tokenManager.getUserTokenConfig() !== null
      });
      return;
    }
    
    console.log('ApoAlgoliaTracking is ready - tracking purchase event');

    // Create unique event key for deduplication (use order total as unique identifier)
    const eventKey = `purchase:${orderTotal}:${source}`;
    if (this.isDuplicateEvent(eventKey)) {
      return;
    }

    const insightsClient = this.clientManager.getInsightsClient();
    this.eventTracker.trackPurchaseEvent(products, orderTotal, source, insightsClient);
  }

  /**
   * Tracks a view event for Algolia insights
   * @param products - Array of product data to track
   * @param source - The source of the view (e.g., 'recommendation', 'userjourney')
   * @param eventName - Custom event name
   * @param queryID - Optional queryID for attribution
   */
  public trackViewEvent(products: ProductSpecData[], source: string = 'recommendation', eventName: string = 'Product Recommendation Viewed', queryID?: string): void {
    if (!this.isReady()) {
      console.warn('ApoAlgoliaTracking not ready for view tracking', {
        isInitialized: this.isInitialized,
        clientReady: this.clientManager.isReady(),
        hasUserToken: this.tokenManager.getUserTokenConfig() !== null
      });
      return;
    }
    
    console.log('ApoAlgoliaTracking is ready - tracking view event');

    // Create unique event key for deduplication based on product SKUs and source
    const productSkus = products.map(p => p.sku).sort().join(',');
    const eventKey = `view:${productSkus}:${source}:${queryID || 'no-query'}`;
    if (this.isDuplicateEvent(eventKey)) {
      return;
    }

    const insightsClient = this.clientManager.getInsightsClient();
    this.eventTracker.trackViewEvent(products, source, eventName, queryID, insightsClient);
  }

  /**
   * Tracks a click event for Algolia insights
   * @param productSku - The SKU of the clicked product
   * @param source - The source of the click (e.g., 'recommendation', 'search')
   * @param eventName - Custom event name
   */
  public trackClickEvent(productSku: string, source: string = 'recommendation', eventName: string = 'Product Recommendation Clicked'): void {
    if (!this.isReady()) {
      console.warn('ApoAlgoliaTracking not ready for click tracking', {
        isInitialized: this.isInitialized,
        clientReady: this.clientManager.isReady(),
        hasUserToken: this.tokenManager.getUserTokenConfig() !== null
      });
      return;
    }
    
    console.log('ApoAlgoliaTracking is ready - tracking click event');

    // Create unique event key for deduplication
    const eventKey = `click:${productSku}:${source}:${eventName}`;
    if (this.isDuplicateEvent(eventKey)) {
      return;
    }

    const insightsClient = this.clientManager.getInsightsClient();
    this.eventTracker.trackClickEvent(productSku, source, eventName, insightsClient);
  }

  /**
   * Tracks search result click events with position and InstantSearch-compatible signature
   * @param productSku - The SKU of the clicked product
   * @param queryID - The query ID from the search
   * @param position - The position of the product in search results (1-based)
   * @param eventName - Custom event name
   * @param query - The search query string
   */
  public trackSearchResultClick(productSku: string, queryID?: string, position?: number, eventName: string = 'Click on Product Item | SRP', query?: string): void {
    if (!this.isReady()) {
      console.warn('ApoAlgoliaTracking not ready for search result click tracking', {
        isInitialized: this.isInitialized,
        clientReady: this.clientManager.isReady(),
        hasUserToken: this.tokenManager.getUserTokenConfig() !== null
      });
      return;
    }
    
    console.log('ApoAlgoliaTracking is ready - tracking search result click');

    // Create unique event key for deduplication
    const eventKey = `search_click:${productSku}:${queryID || 'no-query'}:${position || 0}:${query || 'no-search'}`;
    if (this.isDuplicateEvent(eventKey)) {
      return;
    }

    const insightsClient = this.clientManager.getInsightsClient();
    this.eventTracker.trackSearchResultClick(productSku, queryID, position, eventName, query, insightsClient);
  }

  /**
   * Tracks search result view events (page views/hits viewed)
   * @param productSkus - Array of product SKUs that were viewed
   * @param queryID - The query ID from the search
   * @param eventName - Custom event name
   * @param query - The search query string
   */
  public trackSearchResultView(productSkus: string[], queryID?: string, eventName: string = 'Hits Viewed', query?: string): void {
    if (!this.isReady()) {
      console.warn('ApoAlgoliaTracking not ready for search result view tracking', {
        isInitialized: this.isInitialized,
        clientReady: this.clientManager.isReady(),
        hasUserToken: this.tokenManager.getUserTokenConfig() !== null
      });
      return;
    }
    
    console.log('ApoAlgoliaTracking is ready - tracking search result view');

    // Create unique event key for deduplication
    // For search/category views, deduplicate based primarily on queryID and query
    // This prevents the same search results from being tracked multiple times due to:
    // - React re-renders, - View mode changes (grid/list), - Component re-mounting
    const normalizedQuery = (query || '').toLowerCase().trim();
    const eventKey = `search_view:${queryID || 'no-query'}:${normalizedQuery || 'no-search'}:${eventName}`;
    
    if (this.isDuplicateEvent(eventKey, 1000)) { // Use 1000ms window for view events
      return;
    }

    const insightsClient = this.clientManager.getInsightsClient();
    this.eventTracker.trackSearchResultView(productSkus, queryID, eventName, query, insightsClient);
  }

  /**
   * Tracks autosuggest click events
   * @param productSku - The SKU of the clicked product
   * @param queryID - The query ID from the search
   * @param position - The position of the product in autosuggest results (1-based)
   * @param eventName - Custom event name
   * @param query - The search query string
   */
  public trackAutosuggestClick(productSku: string, queryID?: string, position?: number, eventName: string = 'Click on Product List Item | Autosuggest', query?: string): void {
    if (!this.isReady()) {
      console.warn('ApoAlgoliaTracking not ready for autosuggest click tracking', {
        isInitialized: this.isInitialized,
        clientReady: this.clientManager.isReady(),
        hasUserToken: this.tokenManager.getUserTokenConfig() !== null
      });
      return;
    }
    
    console.log('ApoAlgoliaTracking is ready - tracking autosuggest click');

    // Create unique event key for deduplication
    const eventKey = `autosuggest_click:${productSku}:${queryID || 'no-query'}:${position || 0}:${query || 'no-search'}`;
    if (this.isDuplicateEvent(eventKey)) {
      return;
    }

    const insightsClient = this.clientManager.getInsightsClient();
    this.eventTracker.trackAutosuggestClick(productSku, queryID, position, eventName, query, insightsClient);
  }


  /**
   * Gets the insights client configuration for React InstantSearch
   * @returns Configuration object for React InstantSearch insights or fallback boolean
   */
  public getInsightsConfig(): any {
    if (!this.isReady()) {
      return true;
    }

    const insightsClient = this.clientManager.getInsightsClient();
    if (insightsClient) {
      return {
        insightsClient: insightsClient
      };
    }

    return true;
  }

  // =============================================================================
  // UI Event Management - Legacy support for existing integrations
  // =============================================================================

  /**
   * Initializes tracking event listeners for cart buttons and purchase events
   */
  public initializeEventListeners(): void {
    const pageType = document.body.getAttribute('data-pagetype');

    if (!pageType) {
      return;
    }

    const constants = this.configResolver.getConstants();

    // Initialize for search and product detail pages
    if ((constants.aid !== '' && constants.sak !== '') &&
        (pageType === 'search' || pageType === 'pdp')) {
      this.initialize().catch(error => {
        console.error('Failed to initialize tracking for event listeners:', error);
      });
    }

    // Setup cart button tracking on product detail pages
    if ((constants.aid !== '' && constants.sak !== '') && pageType === 'pdp') {
      window.addEventListener('load', () => {
        this.setupCartButtonTracking();
      });
    }

    // Setup purchase tracking on checkout success page
    // Check for checkout success page regardless of pagetype

    // Check immediately and also with a slight delay in case window variables are set later
    const checkAndSetupPurchaseTracking = () => {
      if ((constants.aid !== '' && constants.sak !== '') && (window as any).CHECKOUT_SUCCESS_PAGE) {
        this.initialize().then(() => {
          this.setupPurchaseTracking();
        }).catch(error => {
          console.error('Failed to initialize tracking for purchase tracking:', error);
        });
        return true;
      }
      return false;
    };

    // Try immediately
    if (!checkAndSetupPurchaseTracking()) {
      // If not found, try again after a short delay
      setTimeout(() => {
        checkAndSetupPurchaseTracking();
      }, 100);
    }
  }

  /**
   * Sets up cart button tracking on product detail pages
   */
  private setupCartButtonTracking(): void {
    // Try desktop button first
    let button = document.querySelector('.cart-button') as HTMLElement;

    // If not found, try mobile button
    if (!button) {
      button = document.querySelector('#product_info_confirm_order_button') as HTMLElement;
    }

    if (button) {
      button.addEventListener('click', (_event: Event) => {
        // Extract PZN from button data attribute or onclick
        const pzn = button.getAttribute('data-pzn') || '';

        // Extract query ID from URL if present (user came from search/category)
        const urlParams = new URLSearchParams(window.location.search);
        const queryId = urlParams.get('queryID') || '';

        // Track the cart button click
        if (pzn) {
          this.trackCartButtonClick(queryId, pzn, 'pdp');
        }
      });
    }
  }

  /**
   * Sets up purchase tracking on checkout success page
   */
  private setupPurchaseTracking(): void {
    // Prevent multiple execution of purchase tracking
    if (this.purchaseTrackingCompleted) {
      return;
    }

    const orderTotals = (window as any).TS_TOTAL_AMOUNT || '';
    const lastCart = (window as any).LAST_CART || '';

    if (orderTotals && lastCart) {
      // Convert the cart array to the format expected by AlgoliaEventTracker
      let formattedProducts: Record<string, any> = {};

      try {
        // Check if lastCart is already a string or needs to be converted
        const cartArray = typeof lastCart === 'string' ? JSON.parse(lastCart) : lastCart;

        if (Array.isArray(cartArray)) {
          cartArray.forEach((product, index) => {
            // Use the product ID as the key and model/SKU for objectIDs
            const productKey = product.id || index.toString();
            formattedProducts[productKey] = {
              model: product.id?.toString() || product.model || '',  // Use product ID for objectIDs
              price: product.price?.toString() || product.final_price?.toString() || '0',
              quantity: product.quantity?.toString() || '1',
              additional_properties: {
                algolia_query_id: product.additional_properties?.algolia_query_id || product.algolia_query_id || ''
              }
            };
          });
        } else {
          console.error('[ApoAlgoliaTracking] lastCart is not an array:', typeof cartArray, cartArray);
          return;
        }

        const productsJson = JSON.stringify(formattedProducts);
        this.trackPurchaseEvent(productsJson, orderTotals.toString());
        
        // Mark purchase tracking as completed to prevent duplicate events
        this.purchaseTrackingCompleted = true;
      } catch (error) {
        console.error('[ApoAlgoliaTracking] Error formatting cart data:', error);
      }
    } else {
      console.warn('Purchase tracking data not available:', {
        orderTotals: !!orderTotals,
        lastCart: !!lastCart
      });
    }
  }
}

// Also expose the individual classes for debugging/development purposes
// This allows direct access to the classes in the browser console
(window as any).AlgoliaTokenManager = AlgoliaTokenManager;
(window as any).AuthenticationDetector = AuthenticationDetector;